import { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { cn } from '../../lib/utils';

type CalloutVariant = 'info' | 'warning' | 'success' | 'error';

interface CalloutProps {
  variant?: CalloutVariant;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}

// A saturated border + a faint tint of the same hue + a bright icon/text reads
// clearly in BOTH themes — the old amber-950/40 fill went muddy on the dark
// content area. Text tone is set per-theme so it stays legible either way.
const VARIANT: Record<CalloutVariant, { box: string; icon: string; Icon: typeof Info }> = {
  info: {
    box: 'border-blue-500/40 bg-blue-500/10 text-blue-900 dark:text-blue-100',
    icon: 'text-blue-600 dark:text-blue-400',
    Icon: Info,
  },
  warning: {
    box: 'border-amber-500/50 bg-amber-500/10 text-amber-900 dark:text-amber-100',
    icon: 'text-amber-600 dark:text-amber-400',
    Icon: AlertTriangle,
  },
  success: {
    box: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-900 dark:text-emerald-100',
    icon: 'text-emerald-600 dark:text-emerald-400',
    Icon: CheckCircle2,
  },
  error: {
    box: 'border-red-500/40 bg-red-500/10 text-red-900 dark:text-red-100',
    icon: 'text-red-600 dark:text-red-400',
    Icon: XCircle,
  },
};

export function Callout({ variant = 'info', title, children, className }: CalloutProps) {
  const { box, icon, Icon } = VARIANT[variant];
  return (
    <div className={cn('flex gap-3 rounded-lg border px-3.5 py-3 text-sm', box, className)}>
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', icon)} />
      <div className="min-w-0 space-y-0.5">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className="[&_strong]:font-semibold">{children}</div>}
      </div>
    </div>
  );
}
